import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner, Alert, Table, Form, Badge, ProgressBar } from "react-bootstrap";
import { Link } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber, nettoyerMontant } from "../utils/formatters";
import './ChiffreAffaires.css';
import { exportCAtoExcel, exportCAtoCSV } from "../utils/excelExport";
import { generateCAPDF } from "../utils/pdfExport";
export default function ChiffreAffaires() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [viewMode, setViewMode] = useState('mensuel');
  const [chartLoading, setChartLoading] = useState(false);

  useEffect(() => {
    fetchChiffreAffaires();
  }, [selectedYear, selectedMonth]);




  const handleExportPDF = async () => {
  try {
    await generateCAPDF(data, selectedYear, selectedMonth);
  } catch (error) {
    alert("Erreur lors de la génération du PDF");
  }
};

const handleExportExcel = () => {
  try {
    exportCAtoExcel(data, selectedYear, selectedMonth);
  } catch (error) {
    alert("Erreur lors de l'export Excel");
  }
};

const handleExportCSV = () => {
  try {
    exportCAtoCSV(data, selectedYear);
  } catch (error) {
    alert("Erreur lors de l'export CSV");
  }
};


  const fetchChiffreAffaires = async () => {
    try {
      setLoading(true);
      setChartLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(
        `https://sawd.julesferry-ga.com/api/ca.php?annee=${selectedYear}&mois=${selectedMonth}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (!response.ok) throw new Error("Erreur de chargement des données");
      
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setChartLoading(false);
    }
  };

  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value));
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(parseInt(e.target.value));
  };

  const getMonthName = (month) => {
    const months = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return months[month - 1];
  };

  // Calculs statistiques
  const caMensuel = data?.annuel?.find(m => m.mois === selectedMonth)?.total_ttc || 0;
const caMensuelNettoye = nettoyerMontant(caMensuel);
  const caJournalier = data?.journalier?.total_ttc || 0;
  const nbFacturesJour = data?.journalier?.nb_factures || 0;
  const panierMoyen = nbFacturesJour > 0 ? caJournalier / nbFacturesJour : 0;
  
const caAnnuel = data?.annuel?.reduce((acc, m) => {
    const montant = typeof m.total_ttc === 'number' 
        ? m.total_ttc 
        : parseFloat(String(m.total_ttc).replace(/[^\d.-]/g, '')) || 0;
    return acc + montant;
}, 0) || 0;
  const tvaAnnuelle = data?.annuel?.reduce((acc, m) => acc + (m.total_tva || 0), 0) || 0;
  const regleAnnuel = data?.annuel?.reduce((acc, m) => acc + (m.total_regle || 0), 0) || 0;
  const tauxEncaissement = caAnnuel > 0 ? (regleAnnuel / caAnnuel) * 100 : 0;

  // Configuration des graphiques
  const getChartOptions = (type) => {
    const baseOptions = {
      chart: {
        toolbar: { show: true },
        zoom: { enabled: true },
        fontFamily: 'Inter, sans-serif'
      },
      grid: {
        borderColor: '#e5e7eb',
        strokeDashArray: 4
      },
      dataLabels: { enabled: false },
      colors: ['#4361ee', '#10b981', '#f59e0b', '#ef4444'],
      tooltip: {
        y: {
          formatter: (value) => formatCurrency(value)
        }
      }
    };

    if (type === 'mensuel') {
      return {
        ...baseOptions,
        chart: { ...baseOptions.chart, type: 'line' },
        xaxis: {
          categories: Array.from({ length: 31 }, (_, i) => i + 1),
          title: { text: 'Jour du mois' }
        },
        yaxis: {
          title: { text: 'Montant (FCFA)' },
          labels: { formatter: (value) => formatCurrency(value) }
        },
        stroke: { curve: 'smooth', width: 3 }
      };
    }

    if (type === 'annuel') {
      return {
        ...baseOptions,
        chart: { ...baseOptions.chart, type: 'bar' },
        xaxis: {
          categories: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
          title: { text: 'Mois' }
        },
        yaxis: {
          title: { text: 'Montant (FCFA)' },
          labels: { formatter: (value) => formatCurrency(value) }
        },
        plotOptions: {
          bar: {
            columnWidth: '60%',
            endingShape: 'rounded'
          }
        }
      };
    }

    if (type === 'evolution') {
      return {
        ...baseOptions,
        chart: { ...baseOptions.chart, type: 'area' },
        xaxis: {
          categories: data?.evolution?.map(item => item.annee) || [],
          title: { text: 'Année' }
        },
        yaxis: {
          title: { text: 'Montant (FCFA)' },
          labels: { formatter: (value) => formatCurrency(value) }
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.3
          }
        }
      };
    }

    if (type === 'repartition') {
      return {
        ...baseOptions,
        chart: { ...baseOptions.chart, type: 'donut' },
        labels: data?.reglements?.map(item => {
          const modes = {
            'especes': 'Espèces',
            'cheque': 'Chèque',
            'virement': 'Virement',
            'carte_bancaire': 'Carte bancaire',
            'traite': 'Traite'
          };
          return modes[item.mode_reglement] || item.mode_reglement;
        }) || [],
        legend: { position: 'bottom' },
        tooltip: {
          y: { formatter: (value) => formatCurrency(value) }
        }
      };
    }

    return baseOptions;
  };

  // Séries pour les graphiques
  const seriesMensuel = [{
    name: 'CA Journalier',
    data: data?.mensuel_detail?.map(item => item.ca_journalier) || Array(31).fill(0)
  }];

  const seriesAnnuel = [{
    name: 'CA Mensuel',
    data: data?.annuel?.map(item => item.total_ttc) || Array(12).fill(0)
  }];

  const seriesEvolution = [{
    name: 'CA Annuel',
    data: data?.evolution?.map(item => item.total_ttc) || []
  }];

  const seriesRepartition = data?.reglements?.map(item => item.total) || [];

  const seriesComparaison = [
    {
      name: `${selectedYear}`,
      data: data?.annuel?.map(item => item.total_ttc) || Array(12).fill(0)
    },
    {
      name: `${selectedYear - 1}`,
      data: data?.annee_precedente?.map(item => item.total_ttc) || Array(12).fill(0)
    }
  ];

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Chargement des données...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Alert variant="danger">
          <Alert.Heading>Erreur</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* En-tête */}
        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/dashboard">Tableau de bord</Link></li>
              <li className="breadcrumb-item active" aria-current="page">Chiffre d'affaires</li>
            </ol>
            <h4 className="main-title mb-0">Analyse du Chiffre d'Affaires</h4>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <div className="d-flex gap-2 mt-3 mt-md-0">
  <Button 
    variant="outline-primary" 
    className="d-flex align-items-center gap-2"
    onClick={handleExportPDF}
  >
    <i className="ri-file-pdf-line fs-18 lh-1"></i>Export PDF
  </Button>
  <Button 
    variant="outline-success" 
    className="d-flex align-items-center gap-2"
    onClick={handleExportExcel}
  >
    <i className="ri-file-excel-line fs-18 lh-1"></i>Export Excel
  </Button>
</div>
          </div>
        </div>

        {/* Filtres */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <Row className="align-items-center">
              <Col md="auto">
                <Form.Label className="mb-0">Période :</Form.Label>
              </Col>
              <Col md="2">
                <Form.Select value={selectedYear} onChange={handleYearChange}>
                  {[2026, 2025, 2024, 2023, 2022].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md="2">
                <Form.Select value={selectedMonth} onChange={handleMonthChange}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <option key={month} value={month}>{getMonthName(month)}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md="auto" className="ms-auto">
                <div className="d-flex gap-2">
                  <Button 
                    variant={viewMode === 'mensuel' ? 'primary' : 'outline-primary'}
                    onClick={() => setViewMode('mensuel')}
                    size="sm"
                  >
                    Journalier
                  </Button>
                  <Button 
                    variant={viewMode === 'annuel' ? 'primary' : 'outline-primary'}
                    onClick={() => setViewMode('annuel')}
                    size="sm"
                  >
                    Mensuel
                  </Button>
                  <Button 
                    variant={viewMode === 'comparaison' ? 'primary' : 'outline-primary'}
                    onClick={() => setViewMode('comparaison')}
                    size="sm"
                  >
                    Comparaison
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Résumé rapide */}
        <Row className="mb-4">
          <Col md={12}>
            <Card className="border-0 shadow-sm bg-light">
              <Card.Body>
                <Row>
                  <Col md={3} className="text-center border-end">
                    <small className="text-muted">Moyenne journalière</small>
                    <h5>{formatCurrency(caMensuelNettoye / 30)}</h5>
                  </Col>
                  <Col md={3} className="text-center border-end">
                    <small className="text-muted">Panier moyen</small>
                    <h5>{formatCurrency(panierMoyen)}</h5>
                  </Col>
                  <Col md={3} className="text-center border-end">
                    <small className="text-muted">TVA collectée</small>
                    <h5 className="text-warning">{formatCurrency(tvaAnnuelle)}</h5>
                  </Col>
                  <Col md={3} className="text-center">
                    <small className="text-muted">Taux d'encaissement</small>
                    <h5 className="text-success">{tauxEncaissement.toFixed(1)}%</h5>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* KPI Cards */}
        <Row className="g-3 mb-4">
          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-primary-soft rounded-circle p-3 me-3" title="Chiffre d'affaires du jour">
                    <i className="ri-money-euro-circle-line fs-24 text-primary"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">CA du jour</h6>
                    <h5 className="mb-0 fw-bold">{formatCurrency(caJournalier)}</h5>
                    <small className="text-secondary">{nbFacturesJour} factures</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-success-soft rounded-circle p-3 me-3">
                    <i className="ri-calendar-line fs-24 text-success"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">CA du mois</h6>
                    <h5 className="mb-0 fw-bold">{formatCurrency(caMensuelNettoye)}</h5>
                    <div className="d-flex align-items-center">
                      <small className="text-secondary me-2">{getMonthName(selectedMonth)} {selectedYear}</small>
                      {data?.comparaison?.evolution && (
                        <span className={`badge ${data.comparaison.evolution >= 0 ? 'bg-success' : 'bg-danger'}`}>
                          <i className={`ri-arrow-${data.comparaison.evolution >= 0 ? 'up' : 'down'}-line me-1`}></i>
                          {Math.abs(data.comparaison.evolution)}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-warning-soft rounded-circle p-3 me-3">
                    <i className="ri-bar-chart-2-line fs-24 text-warning"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">CA annuel</h6>
                    <h5 className="mb-0 fw-bold">{formatCurrency(caAnnuel)}</h5>
                    <small className="text-secondary">{selectedYear}</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-info-soft rounded-circle p-3 me-3">
                    <i className="ri-line-chart-line fs-24 text-info"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">Évolution</h6>
                    <h5 className={`mb-0 fw-bold ${data?.comparaison?.evolution >= 0 ? 'text-success' : 'text-danger'}`}>
                      {data?.comparaison?.evolution >= 0 ? '+' : ''}{data?.comparaison?.evolution || 0}%
                    </h5>
                    <small className="text-secondary">vs année précédente</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Graphiques */}
        <Row className="g-3 mb-4">
          <Col xl="8">
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-transparent border-0 d-flex align-items-center">
                <Card.Title as="h6" className="mb-0">
                  {viewMode === 'mensuel' && `CA Journalier - ${getMonthName(selectedMonth)} ${selectedYear}`}
                  {viewMode === 'annuel' && `CA Mensuel - Année ${selectedYear}`}
                  {viewMode === 'comparaison' && `Évolution du CA sur 5 ans`}
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <div style={{ position: 'relative', minHeight: '400px' }}>
                  {chartLoading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                      <Spinner animation="border" variant="primary" />
                    </div>
                  ) : (
                    <>
                      {viewMode === 'mensuel' && (
                        <ReactApexChart 
                          series={seriesMensuel} 
                          options={getChartOptions('mensuel')} 
                          type="line" 
                          height={400} 
                        />
                      )}
                      {viewMode === 'annuel' && (
                        <ReactApexChart 
                          series={seriesAnnuel} 
                          options={getChartOptions('annuel')} 
                          type="bar" 
                          height={400} 
                        />
                      )}
                      {viewMode === 'comparaison' && (
                        <ReactApexChart 
                          series={seriesEvolution} 
                          options={getChartOptions('evolution')} 
                          type="area" 
                          height={400} 
                        />
                      )}
                    </>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl="4">
            <Card className="border-0 shadow-sm h-100">
              <Card.Header className="bg-transparent border-0">
                <Card.Title as="h6" className="mb-0">Répartition par mode de règlement</Card.Title>
              </Card.Header>
              <Card.Body>
                {data?.reglements?.length > 0 ? (
                  <ReactApexChart 
                    series={seriesRepartition} 
                    options={getChartOptions('repartition')} 
                    type="donut" 
                    height={300} 
                  />
                ) : (
                  <div className="text-center py-5">
                    <i className="ri-pie-chart-2-line fs-48 text-secondary mb-3"></i>
                    <p>Aucune donnée de règlement</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Graphique de comparaison annuelle */}
        {viewMode === 'comparaison' && (
          <Row className="mb-4">
            <Col xl="12">
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-transparent border-0">
                  <Card.Title as="h6" className="mb-0">
                    Comparaison {selectedYear} vs {selectedYear - 1}
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  <div style={{ minHeight: '350px' }}>
                    {chartLoading ? (
                      <div className="d-flex justify-content-center align-items-center" style={{ height: '350px' }}>
                        <Spinner animation="border" variant="primary" />
                      </div>
                    ) : (
                      <ReactApexChart 
                        series={seriesComparaison} 
                        options={{
                          ...getChartOptions('annuel'),
                          colors: ['#4361ee', '#f59e0b']
                        }} 
                        type="line" 
                        height={350} 
                      />
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Tableau détaillé */}
        <Row className="g-3 mb-4">
          <Col xl="8">
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-transparent border-0">
                <Card.Title as="h6" className="mb-0">Détail mensuel du CA {selectedYear}</Card.Title>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table hover className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Mois</th>
                        <th className="text-end">Factures</th>
                        <th className="text-end">Clients</th>
                        <th className="text-end">HT</th>
                        <th className="text-end">TVA</th>
                        <th className="text-end">TTC</th>
                        <th className="text-end">Réglé</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.annuel?.map((mois) => (
                        <tr key={mois.mois}>
                          <td className="fw-semibold">{getMonthName(mois.mois)}</td>
                          <td className="text-end">{formatNumber(mois.nb_factures || 0)}</td>
                          <td className="text-end">{formatNumber(mois.nb_clients || 0)}</td>
                          <td className="text-end text-primary">{formatCurrency(mois.total_ht || 0)}</td>
                          <td className="text-end text-warning">{formatCurrency(mois.total_tva || 0)}</td>
                          <td className="text-end fw-bold">{formatCurrency(mois.total_ttc || 0)}</td>
                          <td className="text-end text-success">{formatCurrency(mois.total_regle || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="table-light fw-bold">
                      <tr>
                        <td>TOTAL</td>
                        <td className="text-end">
                          {formatNumber(data?.annuel?.reduce((acc, m) => acc + (m.nb_factures || 0), 0))}
                        </td>
                        <td className="text-end">-</td>
                        <td className="text-end">
                          {formatCurrency(data?.annuel?.reduce((acc, m) => acc + (m.total_ht || 0), 0))}
                        </td>
                        <td className="text-end">
                          {formatCurrency(data?.annuel?.reduce((acc, m) => acc + (m.total_tva || 0), 0))}
                        </td>
                        <td className="text-end">
                          {formatCurrency(caAnnuel)}
                        </td>
                        <td className="text-end">
                          {formatCurrency(regleAnnuel)}
                        </td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl="4">
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-transparent border-0">
                <Card.Title as="h6" className="mb-0">Top 5 Clients</Card.Title>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table hover className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Client</th>
                        <th className="text-end">CA</th>
                        <th className="text-end">Factures</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.top_clients?.map((client) => (
                        <tr key={client.id_client}>
                          <td>
                            <div>
                              <span className="fw-semibold">{client.raison_sociale}</span>
                              <br />
                              <small className="text-secondary">{client.code_client}</small>
                            </div>
                          </td>
                          <td className="text-end fw-bold text-primary">
                            {formatCurrency(client.total_ttc)}
                          </td>
                          <td className="text-end">
                            <Badge bg="info">{client.nb_factures}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mt-3">
              <Card.Header className="bg-transparent border-0">
                <Card.Title as="h6" className="mb-0">Prévisions</Card.Title>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Mois prochain</span>
                    <span className="fw-bold text-primary">{formatCurrency(data?.previsions?.prevision_mois_prochain || 0)}</span>
                  </div>
                  <ProgressBar now={75} variant="primary" className="mb-2" />
                  
                  <div className="d-flex justify-content-between mb-1">
                    <span>Trimestre</span>
                    <span className="fw-bold text-success">{formatCurrency(data?.previsions?.prevision_trimestre || 0)}</span>
                  </div>
                  <ProgressBar now={85} variant="success" className="mb-2" />
                  
                  <div className="d-flex justify-content-between mb-1">
                    <span>Année</span>
                    <span className="fw-bold text-warning">{formatCurrency(data?.previsions?.prevision_annuelle || 0)}</span>
                  </div>
                  <ProgressBar now={60} variant="warning" />
                </div>

                <hr />

                <div className="text-center text-secondary small">
                  <i className="ri-information-line me-1"></i>
                  Prévisions basées sur la moyenne des 3 derniers mois
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </React.Fragment>
  );
}